import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useWeather } from "../hooks/useWeather";
import AIButton from '../component/ui/AIButton';
import MapContainer from '../component/map/MapContainer';
import playButton from '../img/play_button.png';
import pauseButton from '../img/pause_button.png';
import useGeolocation from '../hooks/useGeolocation';
import sunrise from '../img/sunrise.png';
import sunset from '../img/sunset.png';
import location from '../img/location.png';
import { weatherApi } from '../api/weatherApi';
import wind from '../img/wind.png';
import humidity from '../img/humidity.png';
import rainy from '../img/rainy.png';
import currentPosition from '../img/current_position.png';
import legend from '../img/legend.png';
import InfoBox from '../component/weather/InfoBox';
import Footer from '../component/common/Footer';
import WeatherHeader from '../component/weather/WeatherHeader';
import PlaybackControls from '../component/ui/PlaybackControls';
import WeatherForecastPopup from '../component/weather/WeatherForecastPopup';
import { chatService } from '../services/apiService'; // 추가
import '../css/MainPage.css';

const MainPage = () => {
    const [progress, setProgress] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [intervalId, setIntervalId] = useState(null);
    const [currentAddress, setCurrentAddress] = useState('');
    const [centerAddress, setCenterAddress] = useState('');
    const [latLng, setLatLng] = useState({ lat: 0, lng: 0 });
    const [showForecast, setShowForecast] = useState(false);
    const currentPositionRef = useRef(null);
    const { position } = useGeolocation();
    const { weather, loading } = useWeather(latLng.lat, latLng.lng);
    const [weatherData, setWeatherData] = useState([]);
    
    // 날씨 정보 상태 추가
    const [weatherForecast, setWeatherForecast] = useState('날씨 정보 로딩 중...');
    const [isWeatherLoading, setIsWeatherLoading] = useState(false);
    const lastPositionRef = useRef(null);

    const getAddressFromCoordinates = async (lat, lng) => {
        try {
            const response = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`);
            const data = await response.json();
            
            if (data && data.address) {
                const address = data.address;
                
                // 한국 주소 형식으로 가공
                let formattedAddress = '';
                
                // 행정구역 추출
                const county = address.county || '';
                const city = address.city || address.town || '';
                const suburb = address.suburb || '';
                const neighbourhood = address.neighbourhood || '';
                const road = address.road || '';
                
                // 한국어 주소 형식으로 조합
                if (city) formattedAddress += city + ' ';
                if (county) formattedAddress += county + ' ';
                if (suburb) formattedAddress += suburb + ' ';
                if (neighbourhood) formattedAddress += neighbourhood + ' ';
                if (road) formattedAddress += road;
                
                return formattedAddress.trim();
            }
            return '주소를 찾을 수 없음';
        } catch (error) {
            console.error('Error fetching address:', error);
            return '주소 조회 실패';
        }
    };

    // 날씨 예보 로드 함수
    const loadWeatherForecast = useCallback(async (lat, lng) => {
        if (isWeatherLoading) return;
        
        try {
            setIsWeatherLoading(true);
            console.log('📍 GPS 위치 변경으로 날씨 정보 로드:', lat, lng);
            const forecast = await chatService.getBriefForecast({ lat, lng });
            setWeatherForecast(forecast);
        } catch (error) {
            console.error('날씨 데이터 로드 실패:', error);
            setWeatherForecast('날씨 정보 없음');
        } finally {
            setIsWeatherLoading(false);
        }
    }, [isWeatherLoading]);

    // 위치 변경 감지 및 처리
    useEffect(() => {
        if (position && position.lat && position.lng) {
            // 현재 위치의 주소 가져오기
            getAddressFromCoordinates(position.lat, position.lng)
                .then(address => setCurrentAddress(address));
                
            // 초기 위치 설정 (맵이 처음 로드될 때 현재 위치로 설정)
            if (latLng.lat === 0 && latLng.lng === 0) {
                setLatLng({ lat: position.lat, lng: position.lng });
            }
            
            // GPS 위치가 유의미하게 변경되었는지 확인
            const currentLat = parseFloat(position.lat.toFixed(3));
            const currentLng = parseFloat(position.lng.toFixed(3));
            const lastPos = lastPositionRef.current;
            
            const isNewPosition = !lastPos || 
                Math.abs(lastPos.lat - currentLat) > 0.001 || 
                Math.abs(lastPos.lng - currentLng) > 0.001;
                
            if (isNewPosition) {
                // GPS 위치가 변경되었을 때만 날씨 정보 업데이트
                loadWeatherForecast(currentLat, currentLng);
                lastPositionRef.current = { lat: currentLat, lng: currentLng };
            }
        }
    }, [position, loadWeatherForecast]);

    useEffect(() => {
        if (latLng.lat !== 0 && latLng.lng !== 0) {
            // 지도 중심의 주소 가져오기
            getAddressFromCoordinates(latLng.lat, latLng.lng)
                .then(address => setCenterAddress(address));
        }
    }, [latLng]);

    const handlePlayButtonClick = () => {
        setIsPlaying(!isPlaying);
        
        if (!isPlaying) {
            let currentProgress = progress; 
            const newIntervalId = setInterval(() => {
                currentProgress += 1;
                if (currentProgress >= 100) {
                    clearInterval(newIntervalId);
                    setIntervalId(null);
                    setIsPlaying(false);
                    currentProgress = 0;
                }
                setProgress(currentProgress);
            }, 100);
            setIntervalId(newIntervalId);
        } else {
            if (intervalId) {
                clearInterval(intervalId);
                setIntervalId(null);
            }
        }
    };

    const handleCurrentPositionClick = (callback) => {
        currentPositionRef.current = callback;
    };

    const moveToCurrentPosition = () => {
        if (currentPositionRef.current && position) {
            currentPositionRef.current();
        }
    };

    const handleForecastButtonClick = () => {
        console.log('Fetching weather for:', latLng);
        setShowForecast(true);
    };

    const handleCloseButtonClick = () => {
        setShowForecast(false);
    };

    const extractTownName = (address) => {
        const match = address.match(/(\S+(읍|면|리|동))/);
        return match ? match[0] : '';
    };

    const extractCityName = (address) => {
        const match = address.match(/(\S+(시|군|구))/);
        return match ? match[0] : '';
    };
    
    const fetchWeatherData = async () => {
        try {
            const data = await weatherApi.getWeatherType();
            setWeatherData(data);
        } catch (error) {
            console.error('Error fetching weather data:', error);
        }
    };
    
    const handleAIButtonClick = () => {
        if (position && position.lat && position.lng) {
        }
    }
    
    return (
        <div className="main-container">
            <WeatherHeader 
                currentAddress={currentAddress}
                forecast={weatherForecast}
                isLoading={isWeatherLoading}
            />
            
            <div className="map-container">
                <div className="weather-panel">
                    <div className="weather-controls">
                        <div className="action-buttons">
                            <AIButton onClick={handleAIButtonClick}/>
                        </div>
                    </div>
                    
                    <div className="map-wrapper">
                        <MapContainer 
                            onCurrentPositionClick={handleCurrentPositionClick} 
                            setLatLng={setLatLng} 
                            initialPosition={position}
                        />
                        <div className="position-button-container">
                            <button className="position-button" onClick={moveToCurrentPosition}>
                                <img src={currentPosition} alt="position" className="position-icon" />
                            </button>
                        </div>
                    </div>
                    
                    <PlaybackControls 
                        isPlaying={isPlaying} 
                        playButton={playButton} 
                        pauseButton={pauseButton} 
                        progress={progress} 
                        handlePlayButtonClick={handlePlayButtonClick} 
                    />
                    
                    <div className="legend">
                        <img src={legend} alt="legend" className="legend-image" />
                    </div>
                    <div className="footer text-white text-center py-4 mt-4">
                        <p>자료: 기상청 발표, 디아이랩 제공</p>
                    </div>
                </div>
            </div>
            
            <WeatherForecastPopup 
                showForecast={showForecast}
                handleCloseButtonClick={handleCloseButtonClick}
                centerAddress={centerAddress}
                extractCityName={extractCityName}
                extractTownName={extractTownName}
                weather={weather}
                location={location}
                sunrise={sunrise}
                sunset={sunset}
                wind={wind}
                humidity={humidity}
            />
        </div>
    );
};

export default MainPage;